/**
 * PersonaFloatingBadge — a floating indicator pinned to the bottom-right of the
 * screen that shows the currently active DM/Celeb Room persona (impersonated user).
 *
 * Only visible on DM (`/dm/*`) and Celeb Room (`/celeb-room/*`) pages, and only
 * when a persona is active. Clicking the badge navigates to the User Persona
 * admin page, where the operator can switch to a different persona or sign out.
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserRound, ChevronRight } from 'lucide-react';
import { t } from '../../utils/i18n';
import { useDmPersona } from '../../context/DmPersonaContext';

const PERSONA_PATHS = ['/dm', '/celeb-room', '/game'];
const PERSONA_ADMIN_PATH = '/dm/users';

export default function PersonaFloatingBadge() {
  const { isAuthenticated, username, userId } = useDmPersona();
  const location = useLocation();
  const navigate = useNavigate();

  const onPersonaPage = PERSONA_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
  );

  if (!isAuthenticated || !onPersonaPage) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <button
        type="button"
        onClick={() => navigate(PERSONA_ADMIN_PATH)}
        title={t('header.nav.persona')}
        className="flex items-center gap-2.5 pl-2.5 pr-3 py-2 rounded-full bg-dark-card/95 backdrop-blur border border-primary/40 shadow-[0_8px_30px_rgba(0,0,0,0.5)] hover:border-primary hover:bg-dark-card transition-colors text-left"
      >
        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/15 text-primary shrink-0">
          <UserRound size={15} />
        </span>
        <div className="min-w-0 leading-tight">
          <p className="text-[10px] text-dark-text-muted">{t('header.nav.dmPersonaLabel')}</p>
          <p className="text-xs font-semibold text-primary truncate max-w-[45vw] sm:max-w-[220px]">
            {username || '—'} #{userId ?? '—'}
          </p>
        </div>
        <ChevronRight size={16} className="text-dark-text-muted shrink-0" />
      </button>
    </div>
  );
}
